import type { billing } from '@repletoryreach/sdk-api';

/**
 * Reading the price list for a plan card.
 *
 * A WhatsApp message is priced by where it goes (`wa.marketing.za`,
 * `wa.marketing.ng` …), so a card that quotes "what a message costs" has to
 * choose a country. It is the one every phone field in the console assumes:
 * the organisation's default country, else its own (see `app/PhoneDefaults`).
 */

/** The three message prices a plan card quotes, in the order they read. */
export const SAMPLE_KINDS = ['marketing', 'utility', 'service'] as const;
export type SampleKind = (typeof SAMPLE_KINDS)[number];

export interface SampleRate {
  kind: SampleKind;
  rate: billing.TenantRate;
}

/** The country whose prices are quoted when the organisation's own has no rows on the price list. */
const FALLBACK_COUNTRY = 'za';

/** The sample rows for `country` (ISO 3166-1 alpha-2, any case), else South Africa's. */
export function sampleRates(rates: billing.TenantRate[], country: string | null | undefined): SampleRate[] {
  const byCode = new Map(rates.map((r) => [r.sku_code, r]));
  const wanted = (country ?? '').toLowerCase();
  const suffix = wanted && SAMPLE_KINDS.some((kind) => byCode.has(`wa.${kind}.${wanted}`)) ? wanted : FALLBACK_COUNTRY;
  return SAMPLE_KINDS.flatMap((kind) => {
    const rate = byCode.get(`wa.${kind}.${suffix}`);
    return rate ? [{ kind, rate }] : [];
  });
}

/** What one unit costs on a plan, or undefined when the price list does not say. */
export function planRate(rate: billing.TenantRate, planCode: string): number | undefined {
  return rate.plan_rates_minor?.[planCode];
}

/**
 * Whether a plan is cheaper per message than what the organisation pays now:
 * no sample dearer, and at least one cheaper. A plan the price list has no
 * figures for is never called cheaper.
 */
export function isCheaperPlan(samples: SampleRate[], planCode: string): boolean {
  const prices = samples.map((s) => ({ now: s.rate.rate_minor, then: planRate(s.rate, planCode) }));
  if (prices.length === 0 || prices.some((p) => p.then === undefined)) return false;
  return prices.every((p) => p.then! <= p.now) && prices.some((p) => p.then! < p.now);
}
